/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { ISite } from "../../../../interfaces/sites";
import { useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import useMounted from "../../../../hooks/mounted";
import { debounce } from "lodash";
import { createSite, listSites, showSite, updateSite, deleteSite } from "@/services/infrastructure/sitesService";
import { updateValue } from "@/utils/updateValue";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useCoordinates } from "@/hooks/useCoordinates";
import { useEntitySearch } from "@/hooks/useEntitySearch";
import {
  notifyDeleteResults,
  runDeleteBatch,
} from "@/utils/deleteResults";

const defaultSite: ISite = {
  idCustomer: "",
  code: "",
  assetCode: "",
  state: "",
  customer: {},
  siteCustomers: [],
  status: "",
  remainingArea: null,
  idAccessType: 0,
  type: null,
  city: "",
  address: "",
  cep: "",
  location: {
    type: "Point",
    coordinates: [-46.633309, -23.55052],
  },
  isIAP: false,
  locationIAP: {
    type: "Point",
    coordinates: [0, 0],
  },
  moreMetersIAP: 0,
  zoom: 13,
  region: {
    description: "",
    code: "",
    id: 0
  },
  idRegion: "",
  criticalAccess: false
};

export const states = [
  { id: "AC", name: "Acre" },
  { id: "AL", name: "Alagoas" },
  { id: "AP", name: "Amapá" },
  { id: "AM", name: "Amazonas" },
  { id: "BA", name: "Bahia" },
  { id: "CE", name: "Ceará" },
  { id: "DF", name: "Distrito Federal" },
  { id: "ES", name: "Espírito Santo" },
  { id: "GO", name: "Goiás" },
  { id: "MA", name: "Maranhão" },
  { id: "MT", name: "Mato Grosso" },
  { id: "MS", name: "Mato Grosso do Sul" },
  { id: "MG", name: "Minas Gerais" },
  { id: "PA", name: "Pará" },
  { id: "PB", name: "Paraíba" },
  { id: "PR", name: "Paraná" },
  { id: "PE", name: "Pernambuco" },
  { id: "PI", name: "Piauí" },
  { id: "RJ", name: "Rio de Janeiro" },
  { id: "RN", name: "Rio Grande do Norte" },
  { id: "RS", name: "Rio Grande do Sul" },
  { id: "RO", name: "Rondônia" },
  { id: "RR", name: "Roraima" },
  { id: "SC", name: "Santa Catarina" },
  { id: "SP", name: "São Paulo" },
  { id: "SE", name: "Sergipe" },
  { id: "TO", name: "Tocantins" },
];

export const siteTypes = [
  {
    id: "GREENFIELD",
    name: "Greenfield",
  },
  {
    id: "ROOFTOP",
    name: "Rooftop",
  },
  {
    id: "BIOSITE",
    name: "Biosite",
  },
  {
    id: "MACRO",
    name: "Macro",
  },
  {
    id: "MICRO",
    name: "Micro",
  },
  {
    id: "SMALL_CELL",
    name: "Small Cell",
  },
  {
    id: "MONOPOLE",
    name: "Monopole",
  },
  {
    id: "GUYED_TOWER",
    name: "Guyed Tower",
  },
  {
    id: "SELF_SUPPORTING_TOWER",
    name: "Self-Supporting Tower",
  },
  {
    id: "CAMOUFLAGE_TOWER",
    name: "Camouflage Tower",
  },
  {
    id: "HYBRID",
    name: "Híbrido",
  },
  {
    id: "INDUSTRIAL_BUILDING",
    name: "Prédio Industrial",
  },
  {
    id: "SLS",
    name: "SLS",
  },
  {
    id: "INDOOR",
    name: "Indoor",
  }
];

export const useSites = ({
  disabledShowId,
  disabledGetAll,
  filter: filterInitial = {},
  includes = [],
}: {
  disabledGetAll?: boolean;
  disabledShowId?: boolean;
  filter?: Record<string, any>;
  includes?: string[];
}) => {
  const { idSite: id } = useParams();
  const location = useLocation();
  const [sites, setSites] = useState<ISite[]>([]);
  const [site, setSite] = useState<ISite>(defaultSite);
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial });
  const [totalItems, setTotalItems] = useState<number>(0);
  const [enableChangePositionMap, setEnableChangePositionMap] =
    useState<boolean>(false);
  const { goAdd, goEdit, goBack } = useEntityNavigation({ basePath: "/towermanagement/sites" });

  const [loading, setLoading] = useState<boolean>(false);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSite = (key: string, value: any, custom?: (v: any) => any) => {
    setSite((curr) => {
      let newData = updateValue(curr, key, value);
      if (custom) newData = custom(newData);
      if (key === "isIAP") {
        if (value === true) {
          newData.locationIAP = {
            type: "Point",
            coordinates: [0, 0],
          };
          newData.moreMetersIAP = 0;
        } else {
          newData.locationIAP = {
            type: "Point",
            coordinates: [0, 0],
          };
          newData.moreMetersIAP = 0;
        }
      }
      return { ...curr, ...newData };
    });
  };

  const { handleLat, handleLong } = useCoordinates(setSite);

  const handleIAPLat = (value: any) => {
    setSite((curr) => {
      curr.locationIAP.coordinates[1] = parseFloat(value);
      const newData = curr;
      return { ...curr, ...newData };
    });
  };
  const handleIAPLong = (value: any) => {
    setSite((curr) => {
      curr.locationIAP.coordinates[0] = parseFloat(value);
      const newData = curr;
      return { ...curr, ...newData };
    });
  };
  const getAll = useCallback(
    debounce(
      ({
        page = 1,
        limit,
        filter: filterG = {},
        sort,
      }: {
        page?: number;
        limit?: number;
        filter?: Record<string, any>;
        sort: any;
      }) => {
        setLoading(true);
        listSites({
          includes: ["customer", ...includes],
          page,
          filter: { ...filter, ...filterG },
          limit,
          sort,
        })
          .then(({ sites: listSites, limit, currentPage, totalItems }) => {
            setSites(listSites);

            setLimit(Number(limit));
            setTotalItems(Number(totalItems));
          })
          .finally(() => setLoading(false));
      },
      300
    ),
    []
  );

  const search = useEntitySearch({
    list: listSites,
    key: "sites",
    includes: ["customer", ...includes],
    filter,
  });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showSite({ id, includes })
      .then(({ site: sitesShow }) => {
        setSite(sitesShow);
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createSite({ site })
      .then(({ site: siteCreate }) => {
        toast.success(`Site (${siteCreate.code}) criado com sucesso!`);
        goEdit(siteCreate.id!);
      })
      .finally(() => setLoading(false));
  };

  const update = () => {
    setLoading(true);
    updateSite({ id: site.id!, site })
      .then(({ site: siteUpdate }) => {
        toast.success(`Site (${siteUpdate.code}) atualizado com sucesso!`);
        get({ id: id });
        goEdit(siteUpdate.id!);
      })
      .finally(() => setLoading(false));
  };

  useMounted(() => {
    getAll({ page, limit, filter, sort });
  }, [page, limit, filter, sort]);

  useEffect(() => {
    const isPageSite = location.pathname.includes("/towermanagement/sites");
    if (id && isPageSite && !disabledShowId) {
      if (id !== "new") get({ id: id });
      return;
    }
    if (!disabledGetAll) getAll({ limit, page, filter, sort });
  }, []);

  const deleteMany = async (ids: (string | number)[]) => {
    if (!ids?.length) return;
    setLoading(true);

    try {
      const results = await runDeleteBatch(ids, (entityId) =>
        deleteSite({ id: entityId }),
      );

      notifyDeleteResults(results, {
        singular: "Site",
        plural: "sites",
        gender: "m",
      });
    } catch {
      // erro sem resposta do servidor (rede/timeout): o interceptor já avisou
    } finally {
      setLoading(false);
      getAll({ page, limit, filter, sort });
    }
  };

  return {
    deleteMany,
    siteTypes,
    sites,
    site,
    loading,
    setPage,
    setLimit,
    page,
    limit,
    goEdit,
    goBack,
    goAdd,
    handleSite,
    create,
    totalItems,
    id,
    update,
    get,
    handleLat,
    handleLong,
    enableChangePositionMap,
    setEnableChangePositionMap,
    getAll,
    search,
    handleFilter,
    handleSort,
    handleIAPLat,
    handleIAPLong,
  };
};
